import type {
    ScanReject,
    ScanResult,
    ScannedInstance,
} from '~/domain/dicom/importScan';
import { hasArchive, type ArchiveExpansion } from '~/utils/io/files/archive';
import type { FileTreeResult } from '~/utils/io/files/fileTree';
import {
    allSeriesKeys,
    selectedInstances,
    summarizeScan,
    type ImportOutcome,
    type ImportTarget,
    type PatientDrop,
    type QueueEntryStatus,
    type Verdict,
} from './importModel';
import { archiveRefusalText, dropRefusalText } from './importText';
import type { ImportMessage } from './importMessage';
import type {
    ClinicalContext,
    CommitPhase,
    ImportCommitResult,
} from './commitContract';

/** The disk side of the import wizard as one pure reducer: one patient per queue entry, and generation-tagged results drop when stale. */

/** Refusal shown when files arrive while an upload is in flight. */
export const DROP_WHILE_COMMITTING: ImportMessage = {
    id: 'patientList.import.refusal.importInProgress',
};

/** A verdict that answered about a different patient than the entry it was asked for. */
const MISATTRIBUTED_VERDICT: ImportMessage = {
    id: 'patientList.import.verdict.misattributed',
};

/** Server says the patient exists but withholds the record id, so there is nothing to import into. */
const INCONCLUSIVE_RESOLUTION: ImportMessage = {
    id: 'patientList.import.verdict.inconclusive',
};

/** One patient's whole review. Everything the reviewer edits lives here, never above. */
export type QueueEntry = {
    readonly drop: PatientDrop;
    readonly status: QueueEntryStatus;
    /** Evidence about one patient at one moment: never shared, never carried over. */
    readonly verdict: Verdict;
    readonly notes: string;
    /** Seeded from this entry's own series; no selection exists above the entry. */
    readonly selectedSeries: ReadonlySet<string>;
    /** Every record the server confirmed touching for this patient, across every commit attempt. */
    readonly landed: readonly ImportOutcome[];
    /** Set once an attempt got no answer; from then on `landed` does not bound what was stored. */
    readonly landingUnknown: boolean;
    /** Series the record already holds, by seriesKey. Undefined until read and when a read fails, so a failed read imports everything. */
    readonly alreadyStored?: ReadonlySet<string>;
};

/** The clinical-context fields the review edits, named so the reducer writes one case for both. */
export type ClinicalContextField = 'notes';

export type ImportQueue = {
    /** Never empty: a scan that found no patient becomes `no-dicom`, not an empty queue. */
    readonly entries: readonly QueueEntry[];
    /** Drop-wide, not per patient. */
    readonly rejects: readonly ScanReject[];
    /** Always `0 <= current < entries.length`. */
    readonly current: number;
};

/** Frozen when Import is pressed; the upload reads nothing else. */
export type CommitRequest = {
    readonly entryIndex: number;
    readonly instances: readonly ScannedInstance[];
    readonly seriesKeys: ReadonlySet<string>;
    readonly context: ClinicalContext;
    /** What the record check claimed, kept for reconciliation against what the server did. */
    readonly predicted: ImportTarget;
};

/** What the last settled commit did. Outlives the advance to the next entry. */
export type CommitReport =
    | {
          readonly kind: 'imported';
          readonly entryIndex: number;
          readonly outcome: ImportOutcome;
          /** The server did not land where the record check said it would. */
          readonly contradicted: boolean;
      }
    | {
          readonly kind: 'failed';
          readonly entryIndex: number;
          readonly message: ImportMessage;
          /** Every record the server confirmed touching; empty when nothing landed. */
          readonly landed: readonly ImportOutcome[];
          /** This attempt got no answer, so an empty `landed` asserts nothing about it. */
          readonly landingUnknown: boolean;
      };

export type DiskStage =
    | { readonly kind: 'awaiting-drop' }
    /** Only entered when the selection holds an archive; a plain folder skips straight to scan. */
    | {
          readonly kind: 'expanding';
          readonly files: readonly File[];
          readonly produced: number;
      }
    | {
          readonly kind: 'scanning';
          readonly files: readonly File[];
          readonly scanned: number;
          readonly total: number;
      }
    | { readonly kind: 'no-dicom'; readonly rejects: readonly ScanReject[] }
    | { readonly kind: 'reviewing'; readonly queue: ImportQueue }
    | {
          readonly kind: 'committing';
          readonly queue: ImportQueue;
          readonly request: CommitRequest;
          readonly phase: CommitPhase;
      }
    | { readonly kind: 'done'; readonly queue: ImportQueue };

export type DiskImportState = {
    /** The lifetime owning the queue. Only the wizard's allocator mints values. */
    readonly generation: number;
    /** A drop still being walked. Becomes `generation` only once the walk succeeds. */
    readonly pendingOffer?: number;
    readonly stage: DiskStage;
    readonly isDragging: boolean;
    /** Why the last drop was not taken. Never replaces a live queue, never outlives it. */
    readonly refusal?: ImportMessage;
    readonly report?: CommitReport;
};

export type DiskImportAction =
    | { readonly type: 'drag-changed'; readonly isDragging: boolean }
    | {
          readonly type: 'drop-refused';
          readonly message: ImportMessage;
          /** The walk this refusal is about; absent when the refusal precedes any walk. */
          readonly offer?: number;
      }
    | { readonly type: 'collect-started'; readonly offer: number }
    | {
          readonly type: 'collect-settled';
          readonly offer: number;
          readonly result: FileTreeResult;
      }
    | {
          readonly type: 'expand-progress';
          readonly generation: number;
          readonly produced: number;
      }
    | {
          readonly type: 'expand-settled';
          readonly generation: number;
          readonly result: ArchiveExpansion;
      }
    | {
          readonly type: 'scan-progress';
          readonly generation: number;
          readonly scanned: number;
          readonly total: number;
      }
    | {
          readonly type: 'scan-settled';
          readonly generation: number;
          readonly scan: ScanResult;
      }
    | {
          readonly type: 'verdict-settled';
          readonly generation: number;
          readonly entryIndex: number;
          /** Guard: a verdict answers exactly the patient id it was asked about. */
          readonly dicomPatientId: string;
          readonly verdict: Verdict;
      }
    | {
          readonly type: 'stored-datasets-settled';
          readonly generation: number;
          readonly entryIndex: number;
          /** Guard: the answer describes exactly the patient id it was asked about. */
          readonly dicomPatientId: string;
          readonly stored: ReadonlySet<string>;
      }
    | { readonly type: 'entry-selected'; readonly index: number }
    | { readonly type: 'recheck-requested' }
    | { readonly type: 'series-toggled'; readonly seriesKey: string }
    | {
          readonly type: 'context-changed';
          readonly field: ClinicalContextField;
          readonly value: string;
      }
    | { readonly type: 'commit-requested' }
    | {
          readonly type: 'commit-phase';
          readonly generation: number;
          readonly phase: CommitPhase;
      }
    /** Settles the commit named by the frozen request; there is no second copy of that name. */
    | {
          readonly type: 'commit-settled';
          readonly generation: number;
          readonly result: ImportCommitResult;
      }
    | { readonly type: 'entry-skipped' }
    | { readonly type: 'skip-undone' }
    | { readonly type: 'restarted'; readonly generation: number };

export function initialDiskImportState(generation: number): DiskImportState {
    return {
        generation,
        stage: { kind: 'awaiting-drop' },
        isDragging: false,
    };
}

export function queueOf(stage: DiskStage): ImportQueue | undefined {
    switch (stage.kind) {
        case 'reviewing':
        case 'committing':
        case 'done':
            return stage.queue;
        default:
            return undefined;
    }
}

export function currentEntry(state: DiskImportState): QueueEntry | undefined {
    const queue = queueOf(state.stage);
    return queue === undefined ? undefined : queue.entries[queue.current];
}

/**
 * Whether the disk source holds nothing of its own: no queue, no drop being read, no refusal
 * standing. Restarting the machine here takes nothing away from the reader.
 */
export function isDiskIdle(state: DiskImportState): boolean {
    return (
        state.stage.kind === 'awaiting-drop' &&
        state.pendingOffer === undefined &&
        state.refusal === undefined
    );
}

/** The record check's answer as a verdict; "exists but no record id" fails rather than becoming new-patient. */
export function verdictForResolution(resolution: {
    exists?: boolean;
    patientId?: string | null;
}): Verdict {
    if (!resolution.exists) {
        return { kind: 'resolved', target: { kind: 'new-patient' } };
    }
    if (!resolution.patientId) {
        return { kind: 'failed', message: INCONCLUSIVE_RESOLUTION };
    }
    return {
        kind: 'resolved',
        target: {
            kind: 'existing-patient',
            patientId: resolution.patientId,
        },
    };
}

/** Focusing an entry re-arms its record check; it does not reset the review. */
function focusEntry(queue: ImportQueue, index: number): ImportQueue {
    const entries = queue.entries.map(
        (entry, i): QueueEntry =>
            i === index && entry.status.kind === 'waiting'
                ? { ...entry, verdict: { kind: 'checking' } }
                : entry
    );
    return { ...queue, current: index, entries };
}

function withEntry(
    queue: ImportQueue,
    index: number,
    change: Partial<QueueEntry>
): ImportQueue {
    const entries = queue.entries.map(
        (entry, i): QueueEntry =>
            i === index ? { ...entry, ...change } : entry
    );
    return { ...queue, entries };
}

/** The queue's next screen: the first entry still waiting, or the summary when none is. */
function advanceToNextWaiting(queue: ImportQueue): DiskStage {
    const next = queue.entries.findIndex(
        (entry) => entry.status.kind === 'waiting'
    );
    return next === -1
        ? { kind: 'done', queue }
        : { kind: 'reviewing', queue: focusEntry(queue, next) };
}

/** Whether the server landed somewhere other than the record check predicted. */
function contradicts(predicted: ImportTarget, outcome: ImportOutcome): boolean {
    return predicted.kind === 'new-patient'
        ? !outcome.wasNewPatient
        : outcome.wasNewPatient || outcome.patientId !== predicted.patientId;
}

/** The current entry and its queue when that entry is under review and editable, else undefined. */
function editable(
    state: DiskImportState
): { queue: ImportQueue; entry: QueueEntry } | undefined {
    if (state.stage.kind !== 'reviewing') {
        return undefined;
    }
    const queue = state.stage.queue;
    const entry = queue.entries[queue.current];
    return entry.status.kind === 'waiting' ? { queue, entry } : undefined;
}

function reviewing(
    state: DiskImportState,
    queue: ImportQueue
): DiskImportState {
    return { ...state, stage: { kind: 'reviewing', queue } };
}

/** Retires the refusal for transitions that move the queue past the moment it describes. */
function queueAdvanced(next: DiskImportState): DiskImportState {
    return next.refusal === undefined ? next : { ...next, refusal: undefined };
}

export function diskImportReducer(
    state: DiskImportState,
    action: DiskImportAction
): DiskImportState {
    // A result tagged with an abandoned lifetime is dropped; `restarted` is what mints the new one.
    if (
        action.type !== 'restarted' &&
        'generation' in action &&
        action.generation !== state.generation
    ) {
        return state;
    }

    switch (action.type) {
        case 'drag-changed':
            return state.isDragging === action.isDragging
                ? state
                : { ...state, isDragging: action.isDragging };

        case 'drop-refused':
            // A tagged refusal belongs to one walk; a superseded one must not clear the newer offer.
            if (
                action.offer !== undefined &&
                action.offer !== state.pendingOffer
            ) {
                return state;
            }
            return {
                ...state,
                isDragging: false,
                // Cleared so a late-settling collect walk is dropped, not landed after the refusal.
                pendingOffer: undefined,
                refusal: action.message,
            };

        case 'collect-started':
            return {
                ...state,
                pendingOffer: action.offer,
                refusal: undefined,
            };

        case 'collect-settled': {
            // Orders two overlapping walks: only the most recent offer can take the queue.
            if (action.offer !== state.pendingOffer) {
                return state;
            }
            if (action.result.status !== 'ok') {
                return {
                    ...state,
                    pendingOffer: undefined,
                    refusal: dropRefusalText(action.result),
                };
            }
            // A queue is never replaced while its own files are being uploaded.
            if (state.stage.kind === 'committing') {
                return {
                    ...state,
                    pendingOffer: undefined,
                    refusal: DROP_WHILE_COMMITTING,
                };
            }
            const collected = action.result.files;
            return {
                generation: action.offer,
                isDragging: false,
                stage: hasArchive(collected)
                    ? { kind: 'expanding', files: collected, produced: 0 }
                    : {
                          kind: 'scanning',
                          files: collected,
                          scanned: 0,
                          total: collected.length,
                      },
            };
        }

        case 'expand-progress': {
            if (state.stage.kind !== 'expanding') {
                return state;
            }
            return {
                ...state,
                // Spread so `files` keeps its identity; a fresh reference restarts the expansion.
                stage: { ...state.stage, produced: action.produced },
            };
        }

        case 'expand-settled': {
            if (state.stage.kind !== 'expanding') {
                return state;
            }
            if (action.result.status !== 'ok') {
                return {
                    ...state,
                    stage: { kind: 'awaiting-drop' },
                    refusal: archiveRefusalText(action.result),
                };
            }
            return {
                ...state,
                stage: {
                    kind: 'scanning',
                    files: action.result.files,
                    scanned: 0,
                    total: action.result.files.length,
                },
            };
        }

        case 'scan-progress': {
            if (state.stage.kind !== 'scanning') {
                return state;
            }
            return {
                ...state,
                // Spread so `files` keeps its identity; a fresh reference restarts the scan.
                stage: {
                    ...state.stage,
                    scanned: action.scanned,
                    total: action.total,
                },
            };
        }

        case 'scan-settled': {
            if (state.stage.kind !== 'scanning') {
                return state;
            }
            const summary = summarizeScan(action.scan);
            if (summary.status !== 'ready') {
                return {
                    ...state,
                    stage: { kind: 'no-dicom', rejects: summary.rejects },
                };
            }
            const entries = summary.drops.map(
                (drop): QueueEntry => ({
                    drop,
                    status: { kind: 'waiting' },
                    verdict: { kind: 'checking' },
                    notes: '',
                    selectedSeries: allSeriesKeys(drop.series),
                    landed: [],
                    landingUnknown: false,
                })
            );
            return reviewing(state, {
                entries,
                rejects: summary.rejects,
                current: 0,
            });
        }

        case 'verdict-settled': {
            if (state.stage.kind !== 'reviewing') {
                return state;
            }
            const queue = state.stage.queue;
            const entry = queue.entries[action.entryIndex];
            if (entry === undefined || entry.status.kind !== 'waiting') {
                return state;
            }
            // A verdict about another patient fails the entry loudly rather than being shown.
            const verdict: Verdict =
                entry.drop.identity.dicomPatientId === action.dicomPatientId
                    ? action.verdict
                    : { kind: 'failed', message: MISATTRIBUTED_VERDICT };
            // The previous answer's evidence is retired with the verdict it belonged to: a new
            // answer may name another record, or none, so its marks no longer describe anything.
            // Re-adding exactly the marked keys restores what they withheld and nothing else.
            const retired: Partial<QueueEntry> =
                entry.alreadyStored === undefined
                    ? {}
                    : {
                          alreadyStored: undefined,
                          selectedSeries: new Set([
                              ...entry.selectedSeries,
                              ...entry.alreadyStored,
                          ]),
                      };
            return reviewing(
                state,
                withEntry(queue, action.entryIndex, { ...retired, verdict })
            );
        }

        case 'stored-datasets-settled': {
            if (state.stage.kind !== 'reviewing') {
                return state;
            }
            const queue = state.stage.queue;
            const entry = queue.entries[action.entryIndex];
            if (entry === undefined || entry.status.kind !== 'waiting') {
                return state;
            }
            // A mismatched answer is dropped silently: it only ever removes datasets, so refusing it imports everything.
            if (entry.drop.identity.dicomPatientId !== action.dicomPatientId) {
                return state;
            }
            // A marked series is always exactly a withheld one, so marks and selection move
            // together: the previous marks rejoin the selection before the new ones are taken out,
            // which restores a series the record turns out no longer to hold.
            const selectedSeries = new Set(
                [
                    ...entry.selectedSeries,
                    ...(entry.alreadyStored ?? []),
                ].filter((key) => !action.stored.has(key))
            );
            return reviewing(
                state,
                withEntry(queue, action.entryIndex, {
                    alreadyStored: action.stored,
                    selectedSeries,
                })
            );
        }

        case 'entry-selected': {
            if (state.stage.kind !== 'reviewing') {
                return state;
            }
            const queue = state.stage.queue;
            if (
                action.index === queue.current ||
                action.index < 0 ||
                action.index >= queue.entries.length
            ) {
                return state;
            }
            return queueAdvanced(
                reviewing(state, focusEntry(queue, action.index))
            );
        }

        case 'recheck-requested': {
            const target = editable(state);
            if (target === undefined) {
                return state;
            }
            return reviewing(
                state,
                focusEntry(target.queue, target.queue.current)
            );
        }

        case 'series-toggled': {
            const target = editable(state);
            if (target === undefined) {
                return state;
            }
            const selectedSeries = new Set(target.entry.selectedSeries);
            if (selectedSeries.has(action.seriesKey)) {
                selectedSeries.delete(action.seriesKey);
            } else {
                selectedSeries.add(action.seriesKey);
            }
            return reviewing(
                state,
                withEntry(target.queue, target.queue.current, {
                    selectedSeries,
                })
            );
        }

        case 'context-changed': {
            const target = editable(state);
            if (target === undefined) {
                return state;
            }
            return reviewing(
                state,
                withEntry(target.queue, target.queue.current, {
                    [action.field]: action.value,
                })
            );
        }

        case 'commit-requested': {
            const target = editable(state);
            if (target === undefined) {
                return state;
            }
            const { queue, entry } = target;
            // The footer disables Import short of these; the reducer re-checks since the click and its state differ.
            if (entry.verdict.kind !== 'resolved') {
                return state;
            }
            const instances = selectedInstances(
                entry.drop,
                entry.selectedSeries
            );
            if (instances.length === 0) {
                return state;
            }
            return queueAdvanced({
                ...state,
                report: undefined,
                stage: {
                    kind: 'committing',
                    queue,
                    request: {
                        entryIndex: queue.current,
                        instances,
                        seriesKeys: entry.selectedSeries,
                        // A whitespace-only field is not entered context; sending it would overwrite the patient's own.
                        context: {
                            notes: entry.notes.trim() || null,
                        },
                        predicted: entry.verdict.target,
                    },
                    phase: {
                        kind: 'uploading',
                        totalFiles: instances.length,
                    },
                },
            });
        }

        case 'commit-phase': {
            if (state.stage.kind !== 'committing') {
                return state;
            }
            return {
                ...state,
                // Spread so `request` keeps its identity; a fresh reference re-sends the upload.
                stage: { ...state.stage, phase: action.phase },
            };
        }

        case 'commit-settled': {
            if (state.stage.kind !== 'committing') {
                return state;
            }
            const { queue, request } = state.stage;
            const committed = queue.entries[request.entryIndex];
            if (!action.result.ok) {
                // Files may have landed, so the belief the commit was made on is stale.
                return queueAdvanced({
                    ...state,
                    report: {
                        kind: 'failed',
                        entryIndex: request.entryIndex,
                        message: action.result.message,
                        landed: action.result.landed,
                        landingUnknown: action.result.landingUnknown,
                    },
                    stage: {
                        kind: 'reviewing',
                        queue: withEntry(queue, request.entryIndex, {
                            verdict: { kind: 'checking' },
                            landed: [
                                ...committed.landed,
                                ...action.result.landed,
                            ],
                            // Sticky: a later definitive answer settles its own attempt only.
                            landingUnknown:
                                committed.landingUnknown ||
                                action.result.landingUnknown,
                        }),
                    },
                });
            }
            const outcome = action.result.outcome;
            // The verdict is replaced by the server's own answer, not by what was predicted.
            const imported = withEntry(queue, request.entryIndex, {
                status: {
                    kind: 'imported',
                    outcome,
                    seriesKeys: request.seriesKeys,
                },
                verdict: {
                    kind: 'resolved',
                    target: outcome.wasNewPatient
                        ? { kind: 'new-patient' }
                        : {
                              kind: 'existing-patient',
                              patientId: outcome.patientId,
                          },
                },
                landed: [...committed.landed, outcome],
            });
            return queueAdvanced({
                ...state,
                report: {
                    kind: 'imported',
                    entryIndex: request.entryIndex,
                    outcome,
                    contradicted: contradicts(request.predicted, outcome),
                },
                stage: advanceToNextWaiting(imported),
            });
        }

        case 'entry-skipped': {
            const target = editable(state);
            if (target === undefined) {
                return state;
            }
            return queueAdvanced({
                ...state,
                stage: advanceToNextWaiting(
                    withEntry(target.queue, target.queue.current, {
                        status: { kind: 'skipped' },
                    })
                ),
            });
        }

        case 'skip-undone': {
            if (state.stage.kind !== 'reviewing') {
                return state;
            }
            const queue = state.stage.queue;
            if (queue.entries[queue.current].status.kind !== 'skipped') {
                return state;
            }
            return queueAdvanced(
                reviewing(
                    state,
                    focusEntry(
                        withEntry(queue, queue.current, {
                            status: { kind: 'waiting' },
                        }),
                        queue.current
                    )
                )
            );
        }

        case 'restarted':
            return initialDiskImportState(action.generation);
    }
}
